import 'ldrs/react/Ring.css'

// @ts-expect-error: @rails/request.js is not typed
import { FetchRequest } from '@rails/request.js'
import { Ring } from 'ldrs/react'
import React, { useEffect, useState } from 'react'

import { employersEmployerSettings } from 'rails_routes'
import { useWindowLocation } from 'utils/windowLocation'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog'

function GranularAllowanceView ({ allowanceAmounts }: any) {
  const whoIsCovered = ['employees']

  if (allowanceAmounts[0].granular_spouse_multiplier > 0) {
    whoIsCovered.push('their spouses')
  }
  if (allowanceAmounts[0].granular_dependent_multiplier > 0) {
    whoIsCovered.push('their dependents')
  }

  const whoIsCoveredText = (
    whoIsCovered.length > 1
      ? (
        <>
          {whoIsCovered.slice(0, -1).map((type, index) => (
            <div key={type} className='inline'>
              <span className='font-semibold'>{type}</span>
              {index < whoIsCovered.length - 1 && ', '}
            </div>
          ))}
          {' and '}
          <span className='font-semibold'>{whoIsCovered.slice(-1)}</span>
        </>
        )
      : <span>{whoIsCovered[0]}</span>
  )

  return (
    <div className='flex w-full items-center justify-center my-6'>
      <div className='flex flex-col space-y-4 p-6 text-center'>
        <div className='text-7xl font-semibold text-thatch-purple-secondary'>
          {allowanceAmounts[0].coverage_percentile}%
        </div>
        <div className='text-thatch-gray-secondary text-sm'>
          <div>of available plans are fully covered for your {whoIsCoveredText}.</div>
        </div>
      </div>
    </div>
  )
}

function CustomAllowanceView ({ allowanceAmounts }: any) {
  const hasNonDefaultAllowances = allowanceAmounts.some((allowance: any) => allowance.location_id !== null && allowance.location_type !== null)

  return (
    <div>
      <div className='flex content-evenly'>
        {allowanceAmounts
          .filter((allowance: any) => allowance.location_id === null && allowance.location_type === null)
          .map((allowance: any) => (
            <div key={allowance.id} className='flex w-full items-center justify-center my-6'>
              <div className='flex flex-col flex-grow space-y-4 text-center'>
                <div className='text-4xl font-semibold text-thatch-purple-secondary'>
                  ${Number(allowance.amount.value).toString()}
                </div>
                <div className='text-thatch-gray-secondary text-xs'>
                  <p className=''>
                    {allowance.participant_type === 'employee'
                      ? (
                          'Employee'
                        )
                      : allowance.participant_type === 'spouse'
                        ? (
                            'First dependent'
                          )
                        : allowance.participant_type === 'dependent'
                          ? (
                              'Additional'
                            )
                          : (
                              'Unknown type'
                            )}
                  </p>
                </div>
              </div>
            </div>
          ))}
      </div>
      {hasNonDefaultAllowances && (
        <div className='flex text-xs text-thatch-gray-secondary mb-4 px-8'>
          Your default allowances are shown above. You have other state based allowances which can be viewed on the allowances page.
        </div>
      )}
    </div>
  )
}

function HideAllowanceView () {
  return (
    <div className='flex border rounded-lg p-8 bg-gray-100 text-gray-700 text-sm mb-4'>
      Your allowances were custom created by our team and cannot be shown here. Please reach out to your contact at Thatch if you unsure about your allowances and want to confirm them before sending invites.
    </div>
  )
}

function NoAllowancesView () {
  return (
    <div className='flex border rounded-lg p-8 bg-gray-100 text-gray-700 text-sm mb-4'>
      No allowances were set.
    </div>
  )
}

function AllowancesViewLoading () {
  return (
    <div className='flex w-full items-center justify-center my-12'>
      <Ring size='25' color='#667085' stroke='2.5' />
    </div>
  )
}

type Props = {
  isDialogOpen: boolean
  handleConfirmClick?: () => void
  employerId?: string
  apiType?: 'brokers'
}

function ConfirmAllowancesDialog ({
  isDialogOpen = false,
  handleConfirmClick,
  employerId,
  apiType
}: Props) {
  const { assign } = useWindowLocation()
  const [open, setOpen] = useState(false)
  const [employeeIds, setEmployeeIds] = useState(Array<string>)
  const [isAddAndInviteFlow, setIsAddAndInviteFlow] = useState(false)
  const [allowanceAmounts, setAllowanceAmounts] = useState(null)
  const [allowanceType, setAllowanceType] = useState(null)

  useEffect(() => {
    setOpen(isDialogOpen)
  }, [isDialogOpen])

  const fetchAllowances = async () => {
    try {
      const url = apiType === 'brokers'
        ? `/api/web/brokers/employers/${employerId}/allowances`
        : '/api/allowances'
      // TODO: this method of making an API call is deprecated -- please use reactquery instead
      // see https://www.notion.so/thatchhealth/Using-React-Query-for-Fetching-Data-160114874bc280379918d43442405589
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      })

      if (response.status !== 200) throw new Error('Failed to fetch allowances')
      const json = await response.json()

      const { allowance_type: allowanceType, allowance_amounts: allowanceAmounts } = json.data
      setAllowanceAmounts(allowanceAmounts)
      setAllowanceType(allowanceType)
    } catch (e: any) {
      // TODO: Add error handling.
      console.error(e.message)
    }
  }

  useEffect(() => {
    fetchAllowances()

    // If this argument is passed, it means the dialog is being used in the employee invite v2 flow.
    // In this case, we need to setup any API calls or events that are needed for the invite flow.
    if (!handleConfirmClick) {
      const handleCustomEvent = (event: CustomEvent<{
        open: boolean,
        employeeIds: Array<string>,
        addAndInviteFlow: boolean
      }>) => {
        setOpen(event.detail.open)
        setEmployeeIds(event.detail.employeeIds)
        setIsAddAndInviteFlow(event.detail.addAndInviteFlow)
      }

      window.addEventListener('confirmCurrentAllowances', handleCustomEvent as any)

      return () => {
        window.removeEventListener('confirmCurrentAllowances', handleCustomEvent as any)
      }
    }
  }, [])

  function renderAllowanceView () {
    if (allowanceType === 'hide') {
      return <HideAllowanceView />
    } else if (allowanceType === 'granular') {
      return <GranularAllowanceView allowanceAmounts={allowanceAmounts} />
    } else if (allowanceType === 'custom') {
      return <CustomAllowanceView allowanceAmounts={allowanceAmounts} />
    } else if (allowanceType === 'not_set') {
      return <NoAllowancesView />
    } else {
      // score_tier, metal_tier, household_size, custom_without_default
    }
  }

  const handleConfirmAllowancesClick = async () => {
    // If this argument is passed, it means the dialog is being used in the employee invite v2 flow.
    // In this case, we don't want chain the invite call and let them fill out the v2 form instead.
    if (handleConfirmClick) {
      handleConfirmClick()
      return
    }

    if (isAddAndInviteFlow === true) {
      const confirmEvent = new CustomEvent('allowancesConfirmed', {
        detail: { confirmed: true },
      })
      window.dispatchEvent(confirmEvent)
    } else {
      // TODO: this method of making an API call is deprecated -- please use reactquery instead
    // see https://www.notion.so/thatchhealth/Using-React-Query-for-Fetching-Data-160114874bc280379918d43442405589
      const request = new FetchRequest(
        'post',
        '/employees/invite',
        {
          body: {
            selected_employees: {
              invited_employee_ids: employeeIds,
            },
          },
        }
      )

      const response = await request.perform()
      const json = await response.json
      if (response.ok) {
        assign(json.redirect_to)
      } else {
        // TODO: Add error handling.
        console.warn(json)
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent aria-description='allowances confirmation dialog'>
        {allowanceType === 'not_set'
          ? (
            <DialogHeader>
              <DialogTitle className='h2'>Set your allowances</DialogTitle>
              <DialogDescription>
                You have not set a monthly allowance. Please set your employee allowance before inviting employees to enroll in health benefits.
              </DialogDescription>
              <button onClick={() => { assign(employersEmployerSettings.show({ query: { tab: 'allowances' } })) }} className='button'>Set allowances</button>
            </DialogHeader>
            )
          : (
            <>
              <DialogHeader>
                <DialogTitle className='h2'>Confirm your allowances</DialogTitle>
                <DialogDescription className='DialogDescription'>
                  Before inviting employees to select their health benefits, confirm your allowances are correct and what you&apos;d like to offer.
                </DialogDescription>
              </DialogHeader>
              <div>{allowanceType ? renderAllowanceView() : AllowancesViewLoading()}</div>
              <div className='rounded-lg bg-yellow-100 text-yellow-700 p-4 mb-6 text-sm flex space-x-3'>
                <div className='text-sm'>Once at least one of your employees accepts your health insurance reimbursement offer, your allowances are set for the plan year and <span className='font-semibold'>cannot be changed.</span></div>
              </div>
              <div>
                <button onClick={() => handleConfirmAllowancesClick()} className='button'>Confirm allowances and {handleConfirmClick ? 'continue' : 'invite'}</button>
                <button onClick={() => { assign(employersEmployerSettings.show({ query: { tab: 'allowances' } })) }} className='button-outline mt-4'>View allowances</button>
              </div>
            </>
            )}
      </DialogContent>
    </Dialog>
  )
}

export default ConfirmAllowancesDialog
